import { useTranslation } from "react-i18next";
import { Checkbox } from "./ui/checkbox";
import { DotsThreeVertical, PaperPlaneTilt, Trash, Tag as TagIcon, FilePdf, LinkSimple, Quotes, ChatCircleText, ClockCounterClockwise, Star } from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { downloadCapturesPdf } from "../lib/pdf";
import { formatCaptureDate } from "../lib/formatDate";

export function CaptureCard({ capture, tags, selected, onToggleSelect, onOpen, onDelete, onSendSingle, onAddTag, onGenerateLink, onToggleFavorite }) {
  const { t, i18n } = useTranslation();
  const captureTags = tags.filter((tag) => capture.tags?.includes(tag.id));
  const isText = capture.type === "text";
  const isPdf = capture.type === "pdf";
  const favoritesTag = tags.find((tag) => tag.is_favorites);
  const isFavorite = favoritesTag ? capture.tags?.includes(favoritesTag.id) : false;

  return (
    <div
      data-testid={`capture-card-${capture.id}`}
      className={`group relative border bg-card cursor-pointer transition-transform hover:-translate-y-1 ${selected ? "border-primary brutal-shadow-sm" : "border-border"}`}
      onClick={() => onOpen(capture)}
    >
      <div className="absolute top-2 left-2 z-10" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          data-testid={`capture-select-checkbox-${capture.id}`}
          checked={selected}
          onCheckedChange={() => onToggleSelect(capture.id)}
          className="bg-background border-2"
        />
      </div>

      <div className="absolute top-2 right-2 z-10 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <button
          data-testid={`capture-favorite-toggle-${capture.id}`}
          onClick={() => onToggleFavorite(capture)}
          title={isFavorite ? t("captureCard.unfavorite") : t("captureCard.favorite")}
          className={`h-8 w-8 md:h-7 md:w-7 flex items-center justify-center bg-background/90 border border-border transition-opacity hover:bg-secondary ${isFavorite ? "opacity-100 text-amber-500" : "opacity-100 md:opacity-0 md:group-hover:opacity-100 text-muted-foreground"}`}
        >
          <Star size={16} weight={isFavorite ? "fill" : "regular"} />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid={`capture-action-menu-${capture.id}`}
              className="h-8 w-8 md:h-7 md:w-7 flex items-center justify-center bg-background/90 border border-border opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity hover:bg-secondary"
            >
              <DotsThreeVertical size={16} weight="bold" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-none">
            <DropdownMenuItem data-testid={`capture-action-send-${capture.id}`} onClick={() => onSendSingle(capture)} className="gap-2">
              <PaperPlaneTilt size={14} /> {t("captureCard.sendEmail")}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={`capture-action-tag-${capture.id}`} onClick={() => onAddTag(capture)} className="gap-2">
              <TagIcon size={14} /> {t("captureCard.addTag")}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={`capture-action-pdf-${capture.id}`} onClick={() => downloadCapturesPdf([capture.id])} className="gap-2">
              <FilePdf size={14} /> {t("captureCard.downloadPdf")}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={`capture-action-link-${capture.id}`} onClick={() => onGenerateLink([capture.id])} className="gap-2">
              <LinkSimple size={14} /> {t("captureCard.generateLink")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid={`capture-action-delete-${capture.id}`} onClick={() => onDelete(capture.id)} className="gap-2 text-destructive focus:text-destructive">
              <Trash size={14} /> {t("captureCard.delete")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isText ? (
        <div className="aspect-[4/3] overflow-hidden bg-secondary flex flex-col p-4" data-testid={`capture-text-preview-${capture.id}`}>
          <Quotes size={22} className="text-primary shrink-0 mb-2" weight="fill" />
          {capture.text_content_html ? (
            <div className="rich-text-content text-xs leading-relaxed line-clamp-6" dangerouslySetInnerHTML={{ __html: capture.text_content_html }} />
          ) : (
            <p className="text-xs leading-relaxed line-clamp-6 whitespace-pre-wrap">{capture.text_content}</p>
          )}
        </div>
      ) : (
        <div className="aspect-[4/3] overflow-hidden bg-muted flex items-center justify-center relative">
          <img src={capture.image_data} alt={capture.title} className="w-full h-full object-contain" />
          {isPdf && (
            <div
              className="absolute bottom-2 right-2 flex items-center gap-1 bg-background/90 border border-border text-[10px] font-semibold px-1.5 py-0.5"
              data-testid={`capture-pdf-badge-${capture.id}`}
            >
              <FilePdf size={12} weight="fill" className="text-destructive" /> PDF · {capture.page_count}{t("captureCard.pdfPageCountSuffix")}
            </div>
          )}
        </div>
      )}
      <div className="p-3">
        <p className="font-semibold text-sm truncate">{capture.title || t("captureCard.untitledFallback")}</p>
        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1" data-testid={`capture-dates-${capture.id}`}>
          <span className="flex items-center gap-1" data-testid={`capture-updated-at-${capture.id}`}>
            <ClockCounterClockwise size={12} /> {t("captureCard.updatedAt")} {formatCaptureDate(capture.updated_at, i18n.language)}
          </span>
        </p>
        {capture.comment && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{capture.comment}</p>}
        {capture.reply && (
          <p className="text-xs text-primary line-clamp-2 mt-1 flex items-start gap-1" data-testid={`capture-reply-preview-${capture.id}`}>
            <ChatCircleText size={13} className="shrink-0 mt-0.5" /> {capture.reply}
          </p>
        )}
        {captureTags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {captureTags.map((tag) => (
              <span key={tag.id} className="font-mono-tag text-[9px] bg-accent text-accent-foreground px-1.5 py-0.5">{tag.name}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
